if (!customElements.get('product-cross-swatch')) {
  customElements.define(
    'product-cross-swatch',
    class ProductCrossSwatch extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.productInfo = this.closest('product-info');
        this.variantSelects = this.productInfo?.querySelector('variant-selects');
        this.rememberOriginalProductState();
        this.variants = this.parseVariants();
        this.button?.addEventListener('click', this.handleClick);
        this.productInfo?.addEventListener('change', this.handleVariantInput, true);
        this.restoreFromUrl();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleClick);
        this.productInfo?.removeEventListener('change', this.handleVariantInput, true);
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-related-variants]')?.textContent || '[]');
        } catch (error) {
          console.error('Unable to parse related product variants.', error);
          return [];
        }
      }

      restoreFromUrl() {
        const variantId = new URL(window.location.href).searchParams.get('variant');
        const variant = this.variants.find(({ id }) => String(id) === variantId);
        if (!variant || this.productInfo.dataset.relatedRestoreStarted) return;
        this.productInfo.dataset.relatedRestoreStarted = variantId;
        queueMicrotask(() => this.updateRelatedProduct(variant));
      }

      handleClick = () => {
        if (!this.productInfo?.hasAttribute('data-related-product-active')) this.rememberOriginalProductState(true);
        const size = this.productInfo?.querySelector('.product-form__input--size input:checked')?.value;
        const variant = this.variantForSize(size);
        if (variant) this.updateRelatedProduct(variant);
      };

      handleVariantInput = (event) => {
        if (event.target.closest('.product-form__input--flavor')) {
          if (this.isActive || this.productInfo?._relatedProductRequest) {
            this.cancelRelatedProductRequest();
            this.restoreOriginalProductState();
          }
          return;
        }

        if (!this.isActive || !event.target.closest('.product-form__input--size')) return;
        event.stopImmediatePropagation();
        const variant = this.variantForSize(event.target.value);
        if (variant) this.updateRelatedProduct(variant);
      };

      variantForSize(size) {
        return (
          this.variants.find(({ options }) => options[options.length - 1] === size) ||
          this.variants.find(({ available }) => available) ||
          this.variants[0]
        );
      }

      updateRelatedProduct = async (variant) => {
        if (!this.productInfo || !this.dataset.productUrl) return;

        this.cancelRelatedProductRequest();
        const controller = new AbortController();
        const request = { controller, swatch: this };
        this.productInfo._relatedProductRequest = request;
        this.setActive(true);
        this.button?.classList.add('is-loading');
        this.productInfo.setAttribute('aria-busy', 'true');

        try {
          const separator = this.dataset.productUrl.includes('?') ? '&' : '?';
          const response = await fetch(
            `${this.dataset.productUrl}${separator}variant=${encodeURIComponent(variant.id)}&section_id=${encodeURIComponent(this.dataset.sectionId)}`,
            { signal: controller.signal }
          );
          if (!response.ok) throw new Error(`Product section request failed: ${response.status}`);

          const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (this.productInfo._relatedProductRequest !== request) return;
          const source = responseDocument.querySelector('product-info');
          if (!source) throw new Error('Product section markup was not returned.');

          this.replaceElement(source, '.product-details__description');
          this.replaceProductPackNote(source.querySelector('product-pack-note'));
          this.replacePurchaseOptions(source);
          this.replaceElement(source, 'product-gallery');
          this.replaceSizeSelector(source);
          this.updateVariantSelectsContext(source);

          const formVariantInput = this.productInfo.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
          if (formVariantInput) formVariantInput.value = variant.id;

          this.productInfo.dataset.url = this.dataset.productUrl;
          this.productInfo.dataset.productId = source.dataset.productId;
          this.productInfo.dataset.soldOut = source.dataset.soldOut;

          const sourceButton = source.querySelector('[id^="ProductSubmitButton-"]');
          const sourceButtonText = sourceButton?.querySelector('[data-add-button-text]')?.textContent.trim();
          this.productInfo
            .querySelector('product-add-form')
            ?.toggleSubmitButton(sourceButton?.hasAttribute('disabled') ?? !variant.available, sourceButtonText);

          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);
            if (this.productInfo._relatedProductRequest === request) this.restoreOriginalProductState();
          }
        } finally {
          this.button?.classList.remove('is-loading');
          if (this.productInfo._relatedProductRequest === request) {
            this.productInfo._relatedProductRequest = null;
            this.productInfo.removeAttribute('aria-busy');
          }
        }
      };

      cancelRelatedProductRequest() {
        const request = this.productInfo?._relatedProductRequest;
        if (!request) return;
        request.controller.abort();
        request.swatch?.button?.classList.remove('is-loading');
        this.productInfo._relatedProductRequest = null;
        this.productInfo.removeAttribute('aria-busy');
      }

      replaceElement(source, selector) {
        const currentElement = this.productInfo.querySelector(selector);
        const sourceElement = source.querySelector(selector);
        if (currentElement && sourceElement) currentElement.replaceWith(sourceElement.cloneNode(true));
      }

      replaceSizeSelector(source) {
        const currentSize = this.productInfo.querySelector('.product-form__input--size');
        const sourceSize = source.querySelector('.product-form__input--size');
        if (currentSize && sourceSize) {
          currentSize.replaceWith(sourceSize.cloneNode(true));
        } else if (currentSize) {
          currentSize.remove();
        } else if (sourceSize) {
          const variantSelects = this.productInfo.querySelector('variant-selects');
          variantSelects?.querySelector('[data-selected-variant]')?.before(sourceSize.cloneNode(true));
        }
      }

      updateVariantSelectsContext(source) {
        const sourceVariantSelects = source.querySelector('variant-selects');
        const currentVariantSelects = this.productInfo.querySelector('variant-selects');
        if (!sourceVariantSelects || !currentVariantSelects) return;

        ['productHandle', 'productTitle', 'productId', 'currencyCode', 'selectedPriceAmount'].forEach((key) => {
          if (sourceVariantSelects.dataset[key] == null) {
            delete currentVariantSelects.dataset[key];
          } else {
            currentVariantSelects.dataset[key] = sourceVariantSelects.dataset[key];
          }
        });

        const currentVariantJson = currentVariantSelects.querySelector('[data-selected-variant]');
        const sourceVariantJson = sourceVariantSelects.querySelector('[data-selected-variant]');
        if (currentVariantJson && sourceVariantJson) currentVariantJson.textContent = sourceVariantJson.textContent;
      }

      rememberOriginalProductState(force = false) {
        if (!this.productInfo || (!force && this.productInfo._originalProductState)) return;

        const variantSelects = this.productInfo.querySelector('variant-selects');
        this.productInfo._originalProductState = {
          url: this.productInfo.dataset.url,
          productId: this.productInfo.dataset.productId,
          soldOut: this.productInfo.dataset.soldOut,
          size: variantSelects?.querySelector('.product-form__input--size')?.cloneNode(true),
          packNote: this.productInfo.querySelector('product-pack-note')?.cloneNode(true) || null,
          submitDisabled: this.productInfo.querySelector('[id^="ProductSubmitButton-"]')?.disabled ?? true,
          submitText: this.productInfo.querySelector('[data-add-button-text]')?.textContent.trim(),
          variantJson: variantSelects?.querySelector('[data-selected-variant]')?.textContent,
          variantData: variantSelects
            ? Object.fromEntries(
                ['productHandle', 'productTitle', 'productId', 'currencyCode', 'selectedPriceAmount'].map((key) => [
                  key,
                  variantSelects.dataset[key],
                ])
              )
            : null,
        };
      }

      restoreOriginalProductState() {
        const original = this.productInfo?._originalProductState;
        const variantSelects = this.productInfo?.querySelector('variant-selects');
        if (!original || !variantSelects) return;

        const currentSize = variantSelects.querySelector('.product-form__input--size');
        if (currentSize && original.size) {
          currentSize.replaceWith(original.size.cloneNode(true));
        } else if (currentSize) {
          currentSize.remove();
        } else if (original.size) {
          variantSelects.querySelector('[data-selected-variant]')?.before(original.size.cloneNode(true));
        }

        Object.entries(original.variantData || {}).forEach(([key, value]) => {
          if (value == null) delete variantSelects.dataset[key];
          else variantSelects.dataset[key] = value;
        });

        const variantJson = variantSelects.querySelector('[data-selected-variant]');
        if (variantJson && original.variantJson != null) variantJson.textContent = original.variantJson;

        this.replaceProductPackNote(original.packNote);
        this.productInfo.dataset.url = original.url;
        this.productInfo.dataset.productId = original.productId;
        this.productInfo.dataset.soldOut = original.soldOut;
        this.productInfo.querySelector('product-add-form')?.toggleSubmitButton(original.submitDisabled, original.submitText);
        this.setActive(false);
      }

      replacePurchaseOptions(source) {
        const currentOptions = this.productInfo.querySelector('product-purchase-options');
        const sourceOptions = source.querySelector('product-purchase-options');

        if (currentOptions && sourceOptions) {
          currentOptions.replaceWith(sourceOptions.cloneNode(true));
        } else if (currentOptions) {
          currentOptions.remove();
          this.productInfo.querySelector('form[data-type="add-to-cart-form"] [name="selling_plan"]')?.remove();
        } else if (sourceOptions) {
          this.productInfo.querySelector('[id^="price-"]')?.insertAdjacentElement('afterend', sourceOptions.cloneNode(true));
        }
      }

      replaceProductPackNote(sourceNote) {
        const currentNote = this.productInfo?.querySelector('product-pack-note');

        if (currentNote && sourceNote) {
          currentNote.replaceWith(sourceNote.cloneNode(true));
        } else if (currentNote) {
          currentNote.remove();
        } else if (sourceNote) {
          this.productInfo.querySelector('[id^="price-"]')?.before(sourceNote.cloneNode(true));
        }
      }

      setActive(active) {
        this.productInfo.querySelectorAll('product-cross-swatch').forEach((swatch) => {
          const isCurrent = active && swatch === this;
          swatch.isActive = isCurrent;
          swatch.classList.toggle('is-active', isCurrent);
          swatch.querySelector('button')?.setAttribute('aria-pressed', String(isCurrent));
        });
        this.productInfo.toggleAttribute('data-related-product-active', active);

        if (active) {
          this.productInfo.querySelectorAll('.product-form__input--flavor input:checked').forEach((input) => {
            input.checked = false;
          });
          const selectedValue = this.productInfo.querySelector('.product-form__input--flavor [data-selected-value]');
          if (selectedValue) selectedValue.textContent = this.dataset.label;
        }
      }
    }
  );
}

if (!customElements.get('product-variant-description')) {
  customElements.define(
    'product-variant-description',
    class ProductVariantDescription extends HTMLElement {
      variantChangeUnsubscriber = null;

      connectedCallback() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.sectionId) return;

          const updatedDescription = data.html.querySelector(
            `product-variant-description[data-section-id="${this.dataset.sectionId}"]`
          );
          if (updatedDescription) this.innerHTML = updatedDescription.innerHTML;
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }
    }
  );
}

if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      variantChangeUnsubscriber = null;

      connectedCallback() {
        this.addEventListener('click', this.handleClick);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.sectionId) return;
            const updatedGallery = data.html.querySelector(`product-gallery[data-section-id="${this.dataset.sectionId}"]`);
            if (!updatedGallery) return;
            this.innerHTML = updatedGallery.innerHTML;
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.variantChangeUnsubscriber?.();
      }

      handleClick = (event) => {
        const thumbnail = event.target.closest('[data-gallery-thumbnail]');
        if (thumbnail) this.selectMedia(thumbnail.dataset.galleryThumbnail);
      };

      selectMedia(mediaId) {
        this.querySelectorAll('[data-media-id]').forEach((media) => {
          const isActive = media.dataset.mediaId === mediaId;
          media.hidden = !isActive;
          media.classList.toggle('is-active', isActive);
        });

        this.querySelectorAll('[data-gallery-thumbnail]').forEach((thumbnail) => {
          const isActive = thumbnail.dataset.galleryThumbnail === mediaId;
          thumbnail.setAttribute('aria-current', String(isActive));
          thumbnail.classList.toggle('is-active', isActive);
        });
      }
    }
  );
}

if (!customElements.get('product-quantity')) {
  customElements.define(
    'product-quantity',
    class ProductQuantity extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick = (event) => {
        const button = event.target.closest('button');
        if (!button || !this.input) return;

        const previousValue = this.input.value;
        button.hasAttribute('data-quantity-plus') ? this.input.stepUp() : this.input.stepDown();
        if (this.input.value !== previousValue) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      };
    }
  );
}

if (!customElements.get('product-add-form')) {
  customElements.define(
    'product-add-form',
    class ProductAddForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.buttonText = this.querySelector('[data-add-button-text]');
        this.errorElement = this.querySelector('.product-add-form__error');
        this.form?.addEventListener('submit', this.handleSubmit);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmit);
      }

      handleSubmit = async (event) => {
        event.preventDefault();
        if (!this.form || !this.submitButton || this.submitButton.disabled) return;

        this.handleErrorMessage();
        this.setLoading(true);

        const cartDrawer = document.querySelector('cart-drawer');
        const formData = new FormData(this.form);
        formData.set('quantity', this.quantity);

        if (cartDrawer) {
          formData.set('sections', cartDrawer.getSectionsToRender().map(({ id }) => id).join(','));
          formData.set('sections_url', window.location.pathname);
          cartDrawer.setActiveElement(this.submitButton);
        }

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this item to the cart.');
          }

          if (cartDrawer) {
            cartDrawer.renderContents(result);
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-add-form',
              productVariantId: formData.get('id'),
              cartData: result,
            });
          }
        } catch (error) {
          console.error(error);
          this.handleErrorMessage(error.message);
        } finally {
          this.setLoading(false);
        }
      };

      get quantity() {
        return this.closest('product-info')?.querySelector('.product-quantity__input')?.value || '1';
      }

      setLoading(isLoading) {
        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.toggleAttribute('aria-busy', isLoading);
      }

      handleErrorMessage(message = '') {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = !message;
      }

      toggleSubmitButton(disable = true, text) {
        const isProductSoldOut = this.closest('product-info')?.dataset.soldOut === 'true';

        if (disable || isProductSoldOut) {
          this.submitButton.disabled = true;
          if (isProductSoldOut) {
            this.buttonText.textContent = 'Sold Out';
          } else if (text) {
            this.buttonText.textContent = text;
          }
        } else {
          this.submitButton.disabled = false;
          this.buttonText.textContent = window.variantStrings?.addToCart || 'Add to Cart';
        }
      }
    }
  );
}

if (!customElements.get('product-purchase-options')) {
  customElements.define(
    'product-purchase-options',
    class ProductPurchaseOptions extends HTMLElement {
      variantChangeUnsubscriber = null;
      rechargeRetryTimer = null;
      rechargeRetryCount = 0;

      connectedCallback() {
        this.addEventListener('change', this.handleChange);
        this.addEventListener('click', this.handleCardClick);
        this.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('click', this.handleDocumentClick);
        this.updateSubscriptionDisplay();
        this.syncSelection(this.selectedType);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.sectionId) return;
            const updated = data.html.querySelector('product-purchase-options');
            if (!updated) return;

            const selectedType = this.selectedType;
            this.innerHTML = updated.innerHTML;
            this.dataset.defaultPlan = updated.dataset.defaultPlan;
            const selectedRadio = this.querySelector(`input[value="${selectedType}"]`);
            if (selectedRadio) selectedRadio.checked = true;
            this.updateSubscriptionDisplay();
            this.syncSelection(selectedType);
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('click', this.handleCardClick);
        this.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('click', this.handleDocumentClick);
        this.variantChangeUnsubscriber?.();
        clearTimeout(this.rechargeRetryTimer);
      }

      get selectedType() {
        return this.querySelector('input[type="radio"]:checked')?.value || 'subscription';
      }

      get selectedPlanId() {
        return this.querySelector('[data-plan-select]')?.value || this.dataset.defaultPlan;
      }

      handleChange = (event) => {
        if (event.target.matches('[data-plan-select]')) {
          const subscriptionRadio = this.querySelector('input[value="subscription"]');
          if (subscriptionRadio) subscriptionRadio.checked = true;
          this.updateSubscriptionDisplay();
          this.syncSelection('subscription');
          return;
        }

        if (event.target.matches('input[type="radio"]')) this.syncSelection(event.target.value);
      };

      handleCardClick = (event) => {
        const trigger = event.target.closest('[data-plan-trigger]');
        if (trigger) {
          this.togglePlanDropdown();
          return;
        }

        const planOption = event.target.closest('[data-plan-option]');
        if (planOption) {
          const select = this.querySelector('[data-plan-select]');
          if (select && select.value !== planOption.dataset.planOption) {
            select.value = planOption.dataset.planOption;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.closePlanDropdown();
          return;
        }

        if (event.target.closest('[data-plan-dropdown]')) return;
        const card = event.target.closest('.purchase-options__card');
        const radio = card?.querySelector('input[type="radio"]');
        if (!radio || radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      };

      handleDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closePlanDropdown();
      };

      handleKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const trigger = this.querySelector('[data-plan-trigger]');
        if (trigger?.getAttribute('aria-expanded') !== 'true') return;
        this.closePlanDropdown();
        trigger.focus();
      };

      togglePlanDropdown() {
        const trigger = this.querySelector('[data-plan-trigger]');
        const menu = this.querySelector('[data-plan-menu]');
        if (!trigger || !menu) return;
        const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', String(willOpen));
        menu.hidden = !willOpen;
      }

      closePlanDropdown() {
        const trigger = this.querySelector('[data-plan-trigger]');
        const menu = this.querySelector('[data-plan-menu]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (menu) menu.hidden = true;
      }

      updateSubscriptionDisplay() {
        const option = this.querySelector('[data-plan-select]')?.selectedOptions[0];
        if (!option) return;

        const price = this.querySelector('[data-subscription-price]');
        const compare = this.querySelector('[data-subscription-compare]');
        const saving = this.querySelector('[data-subscription-saving]');
        const label = this.querySelector('[data-plan-label]');

        if (label) label.textContent = option.textContent.trim();
        if (price) price.textContent = option.dataset.planPrice || '';
        if (compare) {
          compare.textContent = option.dataset.planCompare || '';
          compare.hidden = !option.dataset.planCompare;
        }
        if (saving) {
          saving.textContent = option.dataset.planSaving || '';
          saving.hidden = !option.dataset.planSaving;
        }
        this.querySelectorAll('[data-plan-option]').forEach((planOption) => {
          planOption.setAttribute('aria-selected', String(planOption.dataset.planOption === option.value));
        });
      }

      syncSelection(type) {
        const productInfo = this.closest('product-info');
        const form = productInfo?.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        let planInput = form.querySelector('input[name="selling_plan"]');
        if (type === 'subscription') {
          if (!planInput) {
            planInput = document.createElement('input');
            planInput.type = 'hidden';
            planInput.name = 'selling_plan';
            form.append(planInput);
          }
          planInput.value = this.selectedPlanId;
        } else {
          planInput?.remove();
        }

        if (!this.syncRecharge(type, productInfo, this.selectedPlanId)) this.scheduleRechargeRetry();
      }

      scheduleRechargeRetry() {
        clearTimeout(this.rechargeRetryTimer);
        if (this.rechargeRetryCount >= 8) return;
        this.rechargeRetryCount += 1;
        this.rechargeRetryTimer = setTimeout(() => {
          const productInfo = this.closest('product-info');
          if (productInfo && !this.syncRecharge(this.selectedType, productInfo, this.selectedPlanId)) this.scheduleRechargeRetry();
        }, 250);
      }

      syncRecharge(type, productInfo, planId) {
        const rechargeRoot = productInfo.querySelector('.rc-container-wrapper') || document.querySelector('.rc-container-wrapper');
        if (!rechargeRoot) return false;

        const rechargeRadio = rechargeRoot.querySelector(
          type === 'subscription' ? '[data-radio-subsave]' : '[data-radio-onetime]'
        );
        if (rechargeRadio && !rechargeRadio.checked) rechargeRadio.click();

        if (type === 'subscription') {
          const planSelect = rechargeRoot.querySelector('[data-plans-dropdown]');
          if (planSelect && planSelect.value !== planId) {
            planSelect.value = planId;
            planSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        this.rechargeRetryCount = 0;
        return true;
      }
    }
  );
}
